import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  Plus, Edit2, Trash2, Star, Save, X, Loader2, Quote,
  Users, CheckCircle, Sparkles, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

// ── Helpers ────────────────────────────────────────────────────────────────
const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'];
const PROVIDER_SIZES = [
  { value: 'small',  label: 'Small (< 10 staff)' },
  { value: 'medium', label: 'Medium (10 – 50 staff)' },
  { value: 'large',  label: 'Large (50+ staff)' },
];

const AVATAR_GRADIENTS = [
  'from-violet-500 to-purple-600',
  'from-teal-500 to-cyan-600',
  'from-amber-500 to-orange-500',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-green-600',
  'from-pink-500 to-rose-600',
  'from-red-500 to-orange-600',
  'from-sky-500 to-blue-600',
  'from-indigo-500 to-violet-600',
];

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

function avatarGradient(id, idx) {
  return AVATAR_GRADIENTS[(id || idx || 0) % AVATAR_GRADIENTS.length];
}

// ── Star Rating Display ────────────────────────────────────────────────────
function StarDisplay({ rating, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}
        />
      ))}
    </div>
  );
}

// ── Clickable Star Selector ────────────────────────────────────────────────
function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          onMouseEnter={() => setHovered(i)}
          onMouseLeave={() => setHovered(0)}
          className="transition-transform hover:scale-110"
        >
          <Star
            size={24}
            className={clsx(
              'transition-colors',
              i <= (hovered || value)
                ? 'text-amber-400 fill-amber-400'
                : 'text-slate-200 fill-slate-200'
            )}
          />
        </button>
      ))}
      <span className="ml-2 text-sm text-slate-500 font-medium">{value} / 5</span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// MODAL
// ──────────────────────────────────────────────────────────────────────────
function TestimonialModal({ testimonial, onClose, onSaved }) {
  const isEdit = Boolean(testimonial?.id);
  const token = localStorage.getItem('token');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name:          testimonial?.name          || '',
    role:          testimonial?.role          || '',
    company:       testimonial?.company       || '',
    state:         testimonial?.state         || '',
    provider_size: testimonial?.provider_size || '',
    avatar:        testimonial?.avatar        || '',
    quote:         testimonial?.quote         || '',
    rating:        testimonial?.rating        ?? 5,
    is_featured:   testimonial?.is_featured   ?? false,
    is_active:     testimonial?.is_active     !== false,
    sort_order:    testimonial?.sort_order    ?? 0,
  });

  const set = field => e => setForm(f => ({ ...f, [field]: e.target.value }));
  const setCheck = field => e => setForm(f => ({ ...f, [field]: e.target.checked }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.quote) { toast.error('Name and quote are required'); return; }
    setSaving(true);
    try {
      const url = isEdit
        ? `${API}/api/marketing/testimonials/${testimonial.id}`
        : `${API}/api/marketing/testimonials`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          rating:     parseInt(form.rating)     || 5,
          sort_order: parseInt(form.sort_order) || 0,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Save failed');
      toast.success(isEdit ? 'Testimonial updated' : 'Testimonial created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Star size={14} className="text-white fill-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Testimonial' : 'New Testimonial'}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Scrollable form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Name + Role */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Name *</label>
              <input type="text" value={form.name} onChange={set('name')} required
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Role</label>
              <input type="text" value={form.role} onChange={set('role')} placeholder="e.g. CEO"
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
            </div>
          </div>

          {/* Company + State */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Company</label>
              <input type="text" value={form.company} onChange={set('company')} placeholder="Organisation name"
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">State</label>
              <select value={form.state} onChange={set('state')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30 bg-white">
                <option value="">— Select state —</option>
                {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Provider Size */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Provider Size</label>
            <div className="grid grid-cols-3 gap-2">
              {PROVIDER_SIZES.map(({ value, label }) => (
                <label key={value}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all text-sm',
                    form.provider_size === value
                      ? 'border-amber-400 bg-amber-50 text-amber-800 font-semibold'
                      : 'border-slate-200 bg-white text-slate-500 font-medium hover:border-slate-300'
                  )}
                >
                  <input type="radio" name="provider_size" value={value}
                    checked={form.provider_size === value}
                    onChange={set('provider_size')}
                    className="accent-amber-500 shrink-0" />
                  <span className="text-xs leading-tight">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Quote */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Quote *</label>
            <textarea value={form.quote} onChange={set('quote')} rows={4} required
              placeholder="What did they say about ShiftCore?"
              className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none" />
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Rating</label>
            <StarPicker value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
          </div>

          {/* Sort Order */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sort Order</label>
              <input type="number" value={form.sort_order} onChange={set('sort_order')} min={0}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Avatar URL</label>
              <input type="text" value={form.avatar} onChange={set('avatar')} placeholder="https://..."
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
            </div>
          </div>

          {/* Featured + Active */}
          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2.5 cursor-pointer group">
              <input type="checkbox" id="t_featured" checked={form.is_featured} onChange={setCheck('is_featured')}
                className="w-4 h-4 accent-amber-500 rounded" />
              <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700 group-hover:text-amber-700 transition-colors">
                <Star size={14} className={form.is_featured ? 'text-amber-400 fill-amber-400' : 'text-slate-400'} />
                Featured
              </span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer group">
              <input type="checkbox" id="t_active" checked={form.is_active} onChange={setCheck('is_active')}
                className="w-4 h-4 accent-emerald-500 rounded" />
              <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700 group-hover:text-emerald-700 transition-colors">
                <CheckCircle size={14} className={form.is_active ? 'text-emerald-500' : 'text-slate-400'} />
                Active
              </span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-60 shadow-sm">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Testimonial'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ──────────────────────────────────────────────────────────────────────────
export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const token = localStorage.getItem('token');

  async function fetchTestimonials() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/marketing/testimonials/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTestimonials(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchTestimonials(); }, []);

  async function deleteTestimonial(id, name) {
    if (!window.confirm(`Delete testimonial from "${name}"?`)) return;
    try {
      await fetch(`${API}/api/marketing/testimonials/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Deleted');
      fetchTestimonials();
    } catch {
      toast.error('Failed to delete');
    }
  }

  async function toggleFeatured(t) {
    try {
      await fetch(`${API}/api/marketing/testimonials/${t.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...t, is_featured: !t.is_featured }),
      });
      fetchTestimonials();
    } catch {
      toast.error('Failed to update');
    }
  }

  async function toggleActive(t) {
    try {
      await fetch(`${API}/api/marketing/testimonials/${t.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...t, is_active: !t.is_active }),
      });
      fetchTestimonials();
    } catch {
      toast.error('Failed to update');
    }
  }

  const stats = useMemo(() => ({
    total:    testimonials.length,
    featured: testimonials.filter(t => t.is_featured).length,
    active:   testimonials.filter(t => t.is_active).length,
    avgRating: testimonials.length
      ? (testimonials.reduce((s, t) => s + (t.rating || 5), 0) / testimonials.length).toFixed(1)
      : '—',
  }), [testimonials]);

  return (
    <div className="p-6 space-y-5">
      {modal && (
        <TestimonialModal
          testimonial={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={fetchTestimonials}
        />
      )}

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Testimonials</h1>
          <p className="text-slate-500 text-sm mt-0.5">Customer reviews shown on the marketing website</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${MARKETING_URL}/#testimonials`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 transition-colors">
            <ExternalLink size={14} />
            Preview on Site
          </a>
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-sm">
            <Plus size={16} />
            Add Testimonial
          </button>
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total',    value: stats.total,    icon: Users,      color: 'bg-gradient-to-br from-violet-500 to-purple-600' },
          { label: 'Featured', value: stats.featured, icon: Sparkles,   color: 'bg-gradient-to-br from-amber-500 to-orange-500' },
          { label: 'Active',   value: stats.active,   icon: CheckCircle,color: 'bg-gradient-to-br from-emerald-500 to-green-600' },
          { label: 'Avg Rating',value: stats.avgRating,icon: Star,      color: 'bg-gradient-to-br from-yellow-400 to-amber-500' },
        ].map(s => {
          const Ic = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', s.color)}>
                <Ic size={18} className="text-white" />
              </div>
              <div>
                {loading ? (
                  <div className="h-6 w-8 bg-slate-100 rounded animate-pulse mb-0.5" />
                ) : (
                  <p className="text-xl font-bold text-slate-900">{s.value}</p>
                )}
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Cards Grid ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={36} className="animate-spin text-amber-400" />
        </div>
      ) : testimonials.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
            <Quote size={32} className="opacity-30" />
          </div>
          <p className="font-semibold text-slate-500">No testimonials yet</p>
          <p className="text-sm mt-1">Click "Add Testimonial" to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[...testimonials]
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((t, idx) => (
              <TestimonialCard
                key={t.id}
                testimonial={t}
                idx={idx}
                onEdit={() => setModal(t)}
                onDelete={() => deleteTestimonial(t.id, t.name)}
                onToggleFeatured={() => toggleFeatured(t)}
                onToggleActive={() => toggleActive(t)}
              />
            ))}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// CARD COMPONENT
// ──────────────────────────────────────────────────────────────────────────
function TestimonialCard({ testimonial: t, idx, onEdit, onDelete, onToggleFeatured, onToggleActive }) {
  const grad = avatarGradient(t.id, idx);

  return (
    <div className={clsx(
      'bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all duration-200 flex flex-col group',
      t.is_featured
        ? 'border-amber-200 ring-1 ring-amber-200/60'
        : 'border-slate-200'
    )}>
      {/* Card Top */}
      <div className="p-5 flex-1">
        {/* Avatar + Name Row */}
        <div className="flex items-start gap-3 mb-3">
          <div className={clsx(
            'w-12 h-12 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-sm font-bold shrink-0 ring-2 ring-white shadow-md',
            grad
          )}>
            {t.avatar
              ? <img src={t.avatar} alt={t.name} className="w-full h-full rounded-full object-cover" />
              : initials(t.name)
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-900 truncate">{t.name}</p>
            <p className="text-xs text-slate-500 truncate">
              {[t.role, t.company].filter(Boolean).join(', ')}
            </p>
            {t.state && (
              <span className="inline-block mt-0.5 text-[10px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-md">
                {t.state}
              </span>
            )}
          </div>
          {t.is_featured && (
            <span className="shrink-0 flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
              <Star size={9} className="fill-amber-500 text-amber-500" />
              Featured
            </span>
          )}
        </div>

        {/* Stars */}
        <div className="mb-3">
          <StarDisplay rating={t.rating || 5} size={13} />
        </div>

        {/* Quote */}
        <div className="relative">
          <Quote size={18} className="absolute -top-1 -left-0.5 text-slate-200 fill-slate-100 shrink-0" />
          <p className="text-sm text-slate-600 leading-relaxed italic pl-5 line-clamp-3">
            {t.quote}
          </p>
        </div>
      </div>

      {/* Card Footer */}
      <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl flex items-center justify-between gap-2">
        {/* Left: toggles */}
        <div className="flex items-center gap-1.5">
          {/* Featured toggle */}
          <button
            onClick={onToggleFeatured}
            title={t.is_featured ? 'Unfeature' : 'Feature this'}
            className={clsx(
              'p-1.5 rounded-lg transition-all',
              t.is_featured
                ? 'text-amber-500 bg-amber-100 hover:bg-amber-200'
                : 'text-slate-300 hover:text-amber-400 hover:bg-amber-50'
            )}
          >
            <Star size={14} className={t.is_featured ? 'fill-amber-500' : ''} />
          </button>

          {/* Active toggle */}
          <button
            onClick={onToggleActive}
            className={clsx(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all',
              t.is_active
                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-200'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200 border border-slate-200'
            )}
          >
            <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', t.is_active ? 'bg-emerald-500' : 'bg-slate-400')} />
            {t.is_active ? 'Active' : 'Hidden'}
          </button>
        </div>

        {/* Right: Edit + Delete */}
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
            title="Edit"
          >
            <Edit2 size={13} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
